/**
 * `useWikiTree` — fetch the tenant's wiki tree + favorites section.
 *
 * Wraps `GET /api/wiki/tree`. Phase 2 ships a hierarchical envelope:
 *
 *   { favorites: WikiNode[]; tree: WikiNode[] }
 *
 * where each top-level `tree` node carries a recursive `children:
 * WikiNode[]` (the BE walks the hierarchy via a recursive CTE; the
 * `wiki_assert_no_cycle` trigger guarantees no infinite recursion).
 * `favorites` is a flat list of nodes the caller has pinned.
 *
 * The hook exposes three derived shapes downstream:
 *   - `favorites: WikiNode[]` — pinned rows.
 *   - `tree: WikiNode[]` — top-level (parentId === null) hierarchical
 *     forest.
 *   - `nodesById: Map<string, WikiNode>` — flat O(1) lookup across
 *     every node in the tree (favorites are NOT included; they're a
 *     materialized projection of nodes that already live in `tree`).
 *
 * Cache shape: the BE wraps the response in `{ data: { favorites, tree } }`;
 * the queryFn unwraps so cached value is the inner `WikiTreeResponse`.
 *
 * Recursive-type coercion: the generated client emits `WikiTreeNode`
 * with `children: readonly unknown[]` (zod-openapi recursion limit) —
 * the hook does a single boundary cast to the bridge's recursive
 * `WikiNode` so every downstream consumer reads a strongly-typed tree.
 *
 * Realtime: `useGlobalRealtime` invalidates `['wiki', 'tree']` on every
 * `wiki.tree.updated`, `wiki.doc.published`, and `wiki.doc.deleted`
 * frame so a parallel-tab change cascades within one tick.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { apiClient } from '@/api/client'
import type { WikiNode, WikiTreeResponse } from '@/api/types-bridge'

import { WIKI_TREE_QUERY_KEY } from './query-keys'

/** Walk a forest, push every node into `out`. Recursion bounded by the BE depth cap (64). */
function indexNodesByIdInto(out: Map<string, WikiNode>, nodes: readonly WikiNode[]): void {
  for (const node of nodes) {
    out.set(node.id, node)
    if (node.children && node.children.length > 0) {
      indexNodesByIdInto(out, node.children)
    }
  }
}

export interface UseWikiTreeResult {
  data: WikiTreeResponse | undefined
  isLoading: boolean
  isError: boolean
  /** Flat pinned rows the caller has favorited. Empty when the user has none. */
  favorites: readonly WikiNode[]
  /** Top-level forest (every entry has `parentId === null`). */
  tree: readonly WikiNode[]
  /**
   * Flat node-id → node lookup across the entire tree. O(1) reads for
   * breadcrumbs and DnD parent resolution. Excludes favorites (which
   * are projections of nodes already in `tree`).
   */
  nodesById: Map<string, WikiNode>
}

export function useWikiTree(): UseWikiTreeResult {
  const query = useQuery({
    queryKey: WIKI_TREE_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient<{ data: WikiTreeResponse }>('/api/wiki/tree')
      // Single boundary cast — every downstream consumer sees a typed
      // recursive `WikiNode`. The generated `WikiTreeNode.children: unknown[]`
      // is structurally compatible with our `WikiNode.children?: WikiNode[]`
      // shape; the cast just sharpens what zod-openapi can't express.
      return res.data as WikiTreeResponse
    },
    staleTime: 30_000,
  })

  const favorites = query.data?.favorites ?? []
  const tree = query.data?.tree ?? []

  // Derive the flat lookup once per data change so every consumer
  // shares the same Map identity (cheap memo-key for downstream effects).
  const nodesById = useMemo(() => {
    const map = new Map<string, WikiNode>()
    indexNodesByIdInto(map, tree)
    return map
  }, [tree])

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    favorites,
    tree,
    nodesById,
  }
}
